import React from 'react';

import moment from 'moment';
import {ByWeekday, RRule, Weekday} from 'rrule';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {gt} from 'app/utils/i18n';

interface Props {
  byweekday: ByWeekday | ByWeekday[] | null | undefined;
  onChange: (weekdays: Array<Weekday>) => void;
  dueDate: string;
}

export const WEEKDAYS = [
  RRule.MO,
  RRule.TU,
  RRule.WE,
  RRule.TH,
  RRule.FR,
  RRule.SA,
  RRule.SU,
] as const;

export function WeekdaysCheckboxes({
  byweekday,
  onChange,
  dueDate,
}: Props): JSX.Element {
  const localisedWeekDays: Array<{
    val: string;
    key: Weekday;
  }> = React.useMemo(() => {
    // reorder the weekdays to start from SUN
    const keys = WEEKDAYS.slice();
    keys.unshift(keys.pop()!);

    return keys.map((key, idx) => ({
      key: key,
      val: moment().day(idx).format('ddd'),
    }));
  }, []);

  const duedateWeekday = React.useMemo(
    () => moment(dueDate).isoWeekday() - 1,
    [dueDate]
  );

  const normalizedByweekday = React.useMemo(
    () => normalizeByWeekday(byweekday),
    [byweekday]
  );

  return (
    <>
      <label>{gt('Repeats on')}</label>
      <div>
        {localisedWeekDays.map((day) => (
          <LazyTooltip
            key={day.key.weekday}
            hide={day.key.weekday != duedateWeekday}
            tooltip={gt('This day is the due date')}
          >
            <div className="form-check form-check-inline">
              <input
                type="checkbox"
                className="form-check-input"
                id={`weekday-checkbox-${day.key.weekday}`}
                checked={
                  normalizedByweekday
                    .map((w) => w.weekday)
                    .indexOf(day.key.weekday) >= 0
                }
                onChange={(e) =>
                  day.key.weekday != duedateWeekday
                    ? onChange(
                        e.target.checked
                          ? normalizedByweekday.concat([day.key])
                          : normalizedByweekday.filter(
                              (d) => d.weekday !== day.key.weekday
                            )
                      )
                    : null
                }
              />
              <label
                className="form-check-label"
                htmlFor={`weekday-checkbox-${day.key.weekday}`}
              >
                {' '}
                {day.val}
              </label>
            </div>
          </LazyTooltip>
        ))}
      </div>
    </>
  );
}

export function normalizeByWeekday(
  byweekday: Array<ByWeekday> | ByWeekday | null | undefined
): Array<Weekday> {
  /* Normalize to an array of Array<Weekday> */
  if (byweekday == null) {
    return [];
  }
  if (Array.isArray(byweekday)) {
    return byweekday
      .map((w) => normalizeWeekdaySingle(w))
      .sort((a, b) => a.weekday - b.weekday);
  } else {
    return [normalizeWeekdaySingle(byweekday)];
  }
}

function normalizeWeekdaySingle(weekday: ByWeekday): Weekday {
  if (typeof weekday == 'number') {
    return new Weekday(weekday);
  } else if (typeof weekday == 'string') {
    return new Weekday(parseInt(weekday));
  }
  return weekday;
}
